import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Edit05, Plus } from '#app/components/icons/duotone'
import { Check, Dropper, X } from '#app/components/icons/solid'
import { useState } from 'react'
import {
	type Color,
	ColorField,
	type ColorSpace,
	getColorChannels,
	Group,
	Input,
	parseColor,
} from 'react-aria-components'
import { unstable_useRoute } from 'react-router'
import { ClientOnly } from 'remix-utils/client-only'
import {
	AriaColorPicker,
	AriaColorSlider,
	AriaColorSwatch,
	AriaColorThumb,
	AriaColorSliderTrack,
	AriaColorArea,
	ColorSwatchPicker,
	ColorSwatchPickerItem,
} from '#app/components/aria-color.tsx'
import { DOMAIN_CONFIGS, type DomainConfig } from '#app/config/domains.ts'
import { trpc } from '#app/lib/trpc-tanstack.tsx'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'
import { EditColorPalettesModal } from './application/modals/edit-color-palettes-modal'
import { UpgradeModal } from './application/modals/upgrade-modal'
import { Button } from './shared/buttons/button'
import { InputBase } from './shared/input'
import { Select } from './shared/select/select'

export function CustomizeColor({
	color,
	setColor,
	colorSpace,
	setColorSpace,
}: {
	color: Color
	setColor: (color: Color) => void
	colorSpace: ColorSpace | 'hex'
	setColorSpace: (colorSpace: ColorSpace | 'hex') => void
}) {
	const user = useOptionalUser()
	const rootData = unstable_useRoute('root')?.loaderData
	const domainConfig = rootData?.domainConfig || DOMAIN_CONFIGS.localhost

	return (
		<AriaColorPicker
			value={color}
			onChange={setColor}
			aria-label="Color Picker"
		>
			<div className="flex flex-col gap-4">
				<div className="flex items-center justify-between gap-2">
					<h2 className="text-primary text-sm font-semibold">Color</h2>
					<AriaColorSwatch
						className="size-8 rounded-lg"
						aria-label="Selected Color"
					/>
				</div>
				<AriaColorArea
					colorSpace="hsb"
					xChannel="saturation"
					yChannel="brightness"
					aria-label="Color Area"
				>
					<AriaColorThumb className="z-50" aria-label="Color Selector" />
				</AriaColorArea>
				<div className="flex items-center gap-4">
					<ClientOnly>
						{() => {
							// Check browser support.
							// @ts-ignore
							if (typeof EyeDropper === 'undefined') {
								return null
							}

							return (
								<Button
									color="tertiary"
									aria-label="Eye dropper"
									onClick={() => {
										// @ts-ignore
										new EyeDropper()
											.open()
											.then((result: { sRGBHex: string }) => {
												const color = parseColor(result.sRGBHex)
												setColor(color)
											})
									}}
									iconLeading={<Dropper data-icon />}
								/>
							)
						}}
					</ClientOnly>

					<AriaColorSlider
						colorSpace="hsb"
						channel="hue"
						aria-label="Hue Slider"
						className="flex-1"
					>
						<AriaColorSliderTrack>
							<AriaColorThumb className="top-1/2" aria-label="Hue Selector" />
						</AriaColorSliderTrack>
					</AriaColorSlider>
				</div>

				<div className="grid grid-cols-[88px_1fr] gap-4">
					<Select
						selectedKey={colorSpace}
						aria-label="Color Space"
						className="shrink-0"
						onSelectionChange={(value) => {
							if (!value) {
								return
							}

							const stringValue = value.toString()

							if (
								stringValue !== 'hex' &&
								stringValue !== 'rgb' &&
								stringValue !== 'hsl' &&
								stringValue !== 'hsb'
							) {
								return
							}

							setColorSpace(stringValue)
						}}
						items={[
							{
								label: 'HEX',
								id: 'hex',
							},
							{
								label: 'RGB',
								id: 'rgb',
							},
							{
								label: 'HSL',
								id: 'hsl',
							},
							{
								label: 'HSB',
								id: 'hsb',
							},
						]}
					>
						{(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
					</Select>
					{colorSpace === 'hex' ? (
						<ColorField
							className="flex-1"
							name="color"
							aria-label="HEX Color Input"
						>
							<InputBase />
						</ColorField>
					) : (
						<Group className="flex w-full flex-row place-content-center place-items-center">
							{getColorChannels(colorSpace as ColorSpace).map(
								(channel, index) => (
									<ColorField
										colorSpace={colorSpace as ColorSpace}
										channel={channel}
										key={channel}
										aria-label={`${channel.toString()} Input`}
										onChange={(color) => {
											if (!color) {
												return
											}

											setColor(color)
										}}
										className={cn(
											index === 0 && '-mr-px',
											index === 2 && '-ml-px',
										)}
									>
										<Input
											className={({ isFocusVisible }) =>
												cn(
													'text-md text-primary bg-primary ring-border-primary relative w-full px-3 py-2 shadow-xs ring-1 outline-hidden transition-shadow duration-100 ease-linear ring-inset',
													isFocusVisible && 'ring-border-brand z-10 ring-2',
													index === 0 && 'rounded-l-lg',
													index === 1 && 'rounded-none',
													index === 2 && 'rounded-r-lg',
												)
											}
										/>
									</ColorField>
								),
							)}
						</Group>
					)}
				</div>

				{user ? (
					<ColorPalettes color={color} />
				) : (
					<ColorPalettesPlaceholder domainConfig={domainConfig} />
				)}
			</div>
		</AriaColorPicker>
	)
}

function ColorPalettes({ color }: { color: Color }) {
	const queryClient = useQueryClient()
	const [selectedPaletteIdState, setSelectedPaletteId] = useState<
		string | null
	>(null)
	const [isCreating, setIsCreating] = useState(false)
	const [newPaletteName, setNewPaletteName] = useState('')
	const [isEditModalOpen, setIsEditModalOpen] = useState(false)

	const { data: palettes } = useQuery(trpc.colorPalette.list.queryOptions())

	const createMutation = useMutation({
		...trpc.colorPalette.create.mutationOptions(),
		onSuccess: (newPalette) => {
			void queryClient.invalidateQueries({
				queryKey: trpc.colorPalette.list.queryKey(),
			})
			setSelectedPaletteId(newPalette.id)
			setIsCreating(false)
			setNewPaletteName('')
		},
	})

	const updateMutation = useMutation({
		mutationFn: trpc.colorPalette.update.mutationOptions().mutationFn,
		onMutate: async (newData) => {
			await queryClient.cancelQueries({
				queryKey: trpc.colorPalette.list.queryKey(),
			})

			const previousPalettes = queryClient.getQueryData(
				trpc.colorPalette.list.queryKey(),
			)

			queryClient.setQueryData(
				trpc.colorPalette.list.queryKey(),
				(old: typeof previousPalettes) =>
					old?.map((p) =>
						p.id === newData.id
							? { ...p, colors: newData.colors ?? p.colors }
							: p,
					),
			)

			return { previousPalettes }
		},
		onError: (_err, _newData, context) => {
			if (context?.previousPalettes) {
				queryClient.setQueryData(
					trpc.colorPalette.list.queryKey(),
					context.previousPalettes,
				)
			}
		},
	})

	// Derive effective ID - fallback to first palette if no selection
	const selectedPaletteId = selectedPaletteIdState ?? palettes?.[0]?.id ?? null
	const selectedPalette = palettes?.find((p) => p.id === selectedPaletteId)

	const handleSaveColor = () => {
		if (!selectedPaletteId || !selectedPalette) return
		const hexColor = color.toString('hex')
		// Filter out existing color, prepend to front
		const filtered = selectedPalette.colors.filter((c) => c !== hexColor)
		updateMutation.mutate({
			id: selectedPaletteId,
			colors: [hexColor, ...filtered],
		})
	}

	const handleCreatePalette = () => {
		if (!newPaletteName.trim()) return
		createMutation.mutate({ name: newPaletteName.trim() })
	}

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-center justify-between gap-2">
				<h2 className="text-primary text-sm font-semibold">Palettes</h2>
				{palettes && palettes.length > 0 && (
					<Button
						color="tertiary"
						size="sm"
						onClick={() => setIsEditModalOpen(true)}
						iconLeading={Edit05}
						aria-label="Edit palettes"
					/>
				)}
			</div>

			{/* Palette selector */}
			<div className="flex items-center gap-2">
				{palettes && palettes.length > 0 ? (
					<Select
						selectedKey={selectedPaletteId}
						aria-label="Select palette"
						className="flex-1"
						onSelectionChange={(key) => {
							if (key === '__new__') {
								setIsCreating(true)
							} else {
								setSelectedPaletteId(key as string)
							}
						}}
						items={[
							...palettes.map((p) => ({ id: p.id, label: p.name })),
							{ id: '__new__', label: '+ New Palette' },
						]}
					>
						{(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
					</Select>
				) : (
					<Button
						color="secondary"
						size="sm"
						className="w-full"
						onClick={() => setIsCreating(true)}
						iconLeading={Plus}
					>
						Create Palette
					</Button>
				)}
			</div>

			{/* New palette form */}
			{isCreating && (
				<div className="flex items-center gap-2">
					<input
						type="text"
						value={newPaletteName}
						onChange={(e) => setNewPaletteName(e.target.value)}
						placeholder="Palette name"
						className="text-md text-primary bg-primary ring-border-primary flex-1 rounded-lg px-3 py-2 shadow-xs ring-1 outline-hidden ring-inset focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
						onKeyDown={(e) => {
							if (e.key === 'Enter') handleCreatePalette()
							if (e.key === 'Escape') {
								setIsCreating(false)
								setNewPaletteName('')
							}
						}}
						disabled={createMutation.isPending}
						autoFocus
					/>
					<div className="flex items-center gap-1">
						<Button
							color="primary"
							size="sm"
							onClick={handleCreatePalette}
							isLoading={createMutation.isPending}
							iconLeading={Check}
							aria-label="Create palette"
						/>
						<Button
							color="tertiary"
							size="sm"
							onClick={() => {
								setIsCreating(false)
								setNewPaletteName('')
							}}
							iconLeading={X}
							aria-label="Cancel"
						/>
					</div>
				</div>
			)}

			{/* Color swatches */}
			{selectedPalette && (
				<div className="relative min-h-8">
					<button
						type="button"
						onClick={handleSaveColor}
						disabled={updateMutation.isPending}
						className="border-border-secondary hover:border-border-primary absolute top-0 left-0 flex size-8 items-center justify-center rounded-md border-2 border-dashed transition-colors disabled:opacity-50"
						aria-label="Add current color to palette"
					>
						<Plus className="text-fg-quaternary size-4" />
					</button>
					<ColorSwatchPicker aria-label="Palette colors">
						{selectedPalette.colors?.map((c, i) => (
							<ColorSwatchPickerItem
								key={c}
								color={c}
								className={i === 0 ? 'ml-9' : undefined}
							/>
						))}
					</ColorSwatchPicker>
				</div>
			)}

			{palettes && isEditModalOpen && (
				<EditColorPalettesModal
					palettes={palettes}
					onOpenChange={setIsEditModalOpen}
				/>
			)}
		</div>
	)
}

function ColorPalettesPlaceholder({
	domainConfig,
}: {
	domainConfig: DomainConfig
}) {
	return (
		<div className="flex flex-col gap-3">
			<h2 className="text-primary text-sm font-semibold">Palettes</h2>

			<UpgradeModal
				trigger={
					<Button
						color="secondary"
						size="sm"
						className="w-full"
						iconLeading={Plus}
					>
						Create Palette
					</Button>
				}
				domainConfig={domainConfig}
			/>
		</div>
	)
}
